// ============================================================================
// EventEvidenceUpload — attach photos / PDFs to an event (Task #49).
// ============================================================================
// Designed mobile-first: the primary CTA opens the device camera directly
// (<input capture="environment">) so admins can snap a paper sign-in sheet
// in two taps. Desktop falls through to a drag-and-drop zone with a regular
// file picker.
//
// Flow per file:
//   1. POST /api/admin/uploads/sign  → pre-signed S3 PUT URL
//   2. PUT bytes to S3
//   3. POST /api/admin/events/:id/attachments → record in DB
//
// Renders an in-place thumbnail grid of existing attachments below the
// upload controls, with a small archive button on each.
// ============================================================================

import { useEffect, useRef, useState } from 'react';
import {
  Camera, Upload, Loader2, Trash2, FileText, Image as ImageIcon, X, AlertCircle,
} from 'lucide-react';
import { api, type EventAttachment } from '../api';

interface Props {
  calendarEntryId: number;
  token: string;
  // Optional default day_index for new uploads (e.g. when admin is on Day 2's
  // attendance view). Admin can change before each upload.
  defaultDayIndex?: number | null;
  // Total day count to render the day-picker dropdown.
  dayCount?: number;
}

const ALLOWED = 'image/jpeg,image/png,image/webp,image/gif,application/pdf';

export function EventEvidenceUpload({ calendarEntryId, token, defaultDayIndex, dayCount = 1 }: Props) {
  const [attachments, setAttachments] = useState<EventAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [dayIndex, setDayIndex] = useState<number | null>(defaultDayIndex ?? null);
  const [description, setDescription] = useState('');
  const [dragging, setDragging] = useState(false);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const filePickerRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const d = await api.listEventAttachments(token, calendarEntryId);
      setAttachments(d.attachments ?? []);
    } catch (err: any) {
      setError(err?.message || 'Could not load attachments.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [calendarEntryId]);

  // Upload a single file end-to-end.
  const uploadOne = async (file: File) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      setError(`"${file.name}" is larger than 25 MB.`);
      return;
    }
    setBusy(true); setError('');
    try {
      const sign = await api.signUpload(token, {
        filename: file.name,
        content_type: file.type,
        destination: 'evidence',
      });
      const putRes = await fetch(sign.upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!putRes.ok) throw new Error(`S3 PUT failed (${putRes.status})`);
      const kind = file.type === 'application/pdf' ? 'document' : 'evidence';
      await api.createEventAttachment(token, calendarEntryId, {
        day_index: dayIndex,
        kind,
        file_url: sign.public_url,
        filename: file.name,
        content_type: file.type,
        file_size_bytes: file.size,
        description: description.trim() || null,
      });
      // Reset description after each successful upload — but keep dayIndex
      // sticky so a batch of "Day 2" uploads stays on Day 2.
      setDescription('');
      await load();
    } catch (err: any) {
      setError(err?.message || `Could not upload "${file.name}".`);
    } finally {
      setBusy(false);
    }
  };

  const uploadMany = async (files: FileList | File[]) => {
    for (const f of Array.from(files)) {
      // Sequential — keeps presigned URL latency from spiking and avoids
      // hammering SES while the user is on mobile.
      await uploadOne(f);
    }
  };

  const archive = async (att: EventAttachment) => {
    const yes = window.confirm(`Archive "${att.filename}"? The S3 file stays but it'll no longer show on this event.`);
    if (!yes) return;
    try {
      await api.archiveEventAttachment(token, calendarEntryId, att.attachment_id);
      await load();
    } catch (err: any) {
      alert(err?.message || 'Could not archive.');
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.length) {
      uploadMany(e.dataTransfer.files);
    }
  };

  return (
    <div className="space-y-3">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div className="flex-1">{error}</div>
          <button type="button" onClick={() => setError('')} className="text-red-700 hover:text-red-900">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Day / description controls */}
      {dayCount > 1 && (
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-xs font-medium text-gray-700">
            Tag this upload to:
          </label>
          <select
            value={dayIndex ?? ''}
            onChange={(e) => setDayIndex(e.target.value ? Number(e.target.value) : null)}
            className="rounded-md border border-gray-300 px-2 py-1 text-sm"
          >
            <option value="">General (event-wide)</option>
            {Array.from({ length: dayCount }, (_, i) => i + 1).map((d) => (
              <option key={d} value={d}>Day {d}</option>
            ))}
          </select>
        </div>
      )}
      <input
        type="text"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Optional description (e.g. 'Morning sign-in sheet, Day 1')"
        className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
      />

      {/* Camera-first mobile + drag-drop desktop. Both triggers feed the
          same upload pipeline. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="inline-flex items-center justify-center gap-2 rounded-md bg-uif-green text-white font-semibold px-4 py-3 text-sm hover:bg-uif-green-dark cursor-pointer">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}
          {busy ? 'Uploading…' : 'Take photo (mobile camera)'}
          <input
            ref={cameraInputRef}
            type="file"
            accept={ALLOWED}
            capture="environment"
            multiple
            className="hidden"
            disabled={busy}
            onChange={(e) => { const fs = e.target.files; if (fs?.length) uploadMany(fs); (e.target as HTMLInputElement).value = ''; }}
          />
        </label>
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={
            'rounded-md border-2 border-dashed px-4 py-3 text-center transition-colors cursor-pointer ' +
            (dragging
              ? 'border-uif-green bg-uif-green/10 text-uif-green-dark'
              : 'border-gray-300 text-gray-600 hover:border-uif-green/40 hover:bg-gray-50')
          }
          onClick={() => filePickerRef.current?.click()}
        >
          <Upload className="h-5 w-5 mx-auto mb-1" />
          <div className="text-sm font-medium">Drop files or click to browse</div>
          <div className="text-[11px] text-gray-500 mt-0.5">JPEG / PNG / WEBP / PDF · up to 25 MB each</div>
          <input
            ref={filePickerRef}
            type="file"
            accept={ALLOWED}
            multiple
            className="hidden"
            disabled={busy}
            onChange={(e) => { const fs = e.target.files; if (fs?.length) uploadMany(fs); (e.target as HTMLInputElement).value = ''; }}
          />
        </div>
      </div>

      {/* Thumbnail grid of existing attachments */}
      <div>
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 mt-3">
          Attached ({attachments.length})
        </div>
        {loading ? (
          <div className="text-center text-sm text-gray-500 py-4">Loading…</div>
        ) : attachments.length === 0 ? (
          <div className="text-center text-sm text-gray-500 italic py-6 border border-dashed border-gray-200 rounded-md">
            Nothing attached yet. Upload a photo to get started.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {attachments.map((att) => (
              <AttachmentTile key={att.attachment_id} att={att} onArchive={() => archive(att)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AttachmentTile({ att, onArchive }: { att: EventAttachment; onArchive: () => void }) {
  const isImage = att.content_type?.startsWith('image/');
  const sizeKb = att.file_size_bytes ? Math.round(att.file_size_bytes / 1024) : null;
  return (
    <div className="relative border border-gray-200 rounded-md overflow-hidden bg-white group">
      <a href={att.file_url} target="_blank" rel="noopener noreferrer" className="block">
        {isImage ? (
          <img src={att.file_url} alt={att.filename} className="w-full h-32 object-cover" />
        ) : (
          <div className="w-full h-32 flex items-center justify-center bg-gray-50 text-gray-500">
            {att.content_type === 'application/pdf'
              ? <FileText className="h-10 w-10" />
              : <ImageIcon className="h-10 w-10" />}
          </div>
        )}
      </a>
      <div className="px-2 py-1.5 border-t border-gray-200 text-[11px]">
        <div className="font-medium text-gray-900 truncate" title={att.filename}>{att.filename}</div>
        <div className="text-gray-500 flex items-center justify-between">
          <span>
            {att.day_index ? `Day ${att.day_index}` : 'General'}
            {sizeKb && ` · ${sizeKb} KB`}
          </span>
          <button
            type="button"
            onClick={onArchive}
            className="opacity-0 group-hover:opacity-100 text-red-700 hover:text-red-900 transition-opacity"
            title="Archive attachment"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
        {att.description && <div className="text-gray-600 truncate" title={att.description}>{att.description}</div>}
      </div>
    </div>
  );
}
